import React, { useState, useEffect } from 'react'
import ScaleBar from './ScaleBar'
import ScaleBarDiffGraph from './ScaleBarDiffGraph'
import TableGrid from './TableGrid'
import TableGridGraph from './TableGridDiffGraph'
import { Text, View, StyleSheet, Font, Line, Svg } from '@react-pdf/renderer'

Font.registerHyphenationCallback(word => [word]);

const styles = StyleSheet.create({
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingLeft: '5%',
    paddingRight: '5%',
    backgroundColor: 'rgba(189,187,183,0.25)',
  },
  tableHeaderTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 5,
    paddingBottom: 5,
  },
  tableHeaderTitleText1: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: 500,
    marginRight: 5,
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottom: '1 solid rgb(226 232 240)',
  },
  tableFirstCol: {
    width: '50%',
    backgroundColor: '#ffffff',
  },
  tableFirstColAnswer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  tableFirstColAnswerNumber: {
    width: '10%',
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
  },
  tableFirstColAnswerText: {
    display: 'flex',
    flexDirection: 'row',
    width: '80%',
    paddingRight: 10,
    paddingTop: 3,
    paddingBottom: 3,
  },
  tableFirstColAnswerPoints: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '10%',
    height: '100%',
    textAlign: 'right',
    justifyContent: 'flex-end',
    paddingLeft: 3,
    paddingRight: 10,
  },
  tableLastCol: {
    width: '50%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgb(226 232 240)',
    borderBottom: '1 solid #fff',
    paddingRight: 7,
    opacity: 0.7,
  },
  tableLastColRows: {
    width: '100%',
    flexDirection: 'row',
    height: 15,
  },
  scaleBarContainer: {
    position: 'relative',
    zIndex: 50,
    display: 'flex',
    flexDirection: 'row',
  },
  gridContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '5.5%',
    top: -1,
    display: 'flex',
    flexDirection: 'row',
    borderTop: '1 solid rgb(226 232 240)',
  },
  legendContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: '5%',
    paddingRight: '5%',
    marginTop: 20,
  },
  legendText: {
    fontFamily: 'Inter',
    fontSize: 8,
    fontWeight: 400,
  },
  legendLine: {
    height: 1.5,
    width: 5,
    backgroundColor: '#000',
  },
  legendDot: {
    height: 5,
    width: 5,
    backgroundColor: '#000',
    borderRadius: 20,
  }
});

const OverviewTable = ({
  answersData,
  categoriesData,
  uiData,
  matchCategory,
  questionsOn,
  coverData,
  add2GaugePage
}) => {
  const [matchedCategory, setMatchedCategory] = useState(null);
  const [answersFromCategory, setAnswersFromCategory] = useState([]);

  useEffect(() => {
    if (answersData && categoriesData && matchCategory) {
      const categoryData = categoriesData.find(item => item.name === matchCategory);
      setMatchedCategory(categoryData);
      const result = answersData.filter(item => item.category === matchCategory);
      setAnswersFromCategory(result);
    }
  }, [answersData, categoriesData, matchCategory]);

  const category = categoriesData.find(cat => cat.name === matchCategory);
  let profiles = category ? [...category.profiles] : [];

  const totalPoints = profiles.reduce((sum, profile) => sum + profile.points, 0);
  const averagePoints = profiles.length > 0 ? Math.round(totalPoints / profiles.length) : 0;

  let profilesForGraph = [];

  if (
    add2GaugePage &&
    coverData &&
    Array.isArray(coverData.profile) &&
    coverData.profile.length === 2
  ) {
    const [firstName, secondName] = coverData.profile;

    const firstProfile = profiles.find(p => p.profile === firstName);
    const secondProfile = profiles.find(p => p.profile === secondName);

    if (firstProfile) {
      profilesForGraph.push({ profile: firstProfile.profile, points: firstProfile.points });
    }

    profilesForGraph.push({ profile: uiData.average, points: averagePoints });

    if (secondProfile) {
      profilesForGraph.push({ profile: secondProfile.profile, points: secondProfile.points });
    }

    if (profilesForGraph.length === 0) {
      profilesForGraph = [...profiles];
    }
  } else {
    profiles = profiles.sort((a, b) => b.points - a.points);

    let inserted = false;
    const tmp = [];
    for (let i = 0; i < profiles.length; i++) {
      if (!inserted && profiles[i].points < averagePoints) {
        tmp.push({ profile: uiData.average, points: averagePoints });
        inserted = true;
      }
      tmp.push(profiles[i]);
    }
    if (!inserted) {
      tmp.push({ profile: uiData.average, points: averagePoints });
    }
    profilesForGraph = tmp;
  }

  const calculateXPosition = point => {
    return point !== undefined ? ((point + 14) / 5) * 10 + 2 : 0;
  };

  return (
    <>
      <View style={styles.tableHeader}>
        <View style={styles.tableHeaderTitle}>
          <Text style={styles.tableHeaderTitleText1}>
            {uiData.header01}: {matchCategory}
          </Text>
        </View>
      </View>

      <View
        style={{
          position: 'relative',
          paddingLeft: '5%',
          paddingRight: '5%',
          marginTop: 40,
          marginBottom: 35,
          width: '100%',
        }}
      >
        <View style={styles.gridContainer} zIndex={9999}>
          <View style={{ width: '50%' }}></View>
          <View style={{ width: '50%' }}>
            <TableGrid />
          </View>
        </View>

        {profilesForGraph.map((entry, index) => {
          const isAverage = entry.profile === uiData.average;
          return (
            <View key={index}>
              <View style={styles.tableContainer}>
                <View style={styles.tableFirstCol}>
                  <View style={styles.tableFirstColAnswer}>
                    <View style={styles.tableFirstColAnswerText}>
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 10,
                          fontWeight: 500,
                          lineHeight: 1.2,
                          color: isAverage ? '#3395E0' : '#000',
                        }}
                      >
                        {entry.profile}
                      </Text>
                    </View>
                    <View style={styles.tableFirstColAnswerPoints}>
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 10,
                          fontWeight: 700,
                          color: isAverage ? '#3395E0' : '#000',
                        }}
                      >
                        {entry.points}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.tableLastCol}>
                  <View style={styles.tableLastColRows}>
                    <View
                      style={{
                        height: 15,
                        borderTopRightRadius: 5,
                        borderBottomRightRadius: 5,
                        width: entry.points + '%',
                        backgroundColor: isAverage
                          ? '#3395E0'
                          : entry.points <= 35
                            ? '#dc2626'
                            : entry.points <= 50
                              ? '#eac714'
                              : entry.points <= 65
                                ? '#7bb662'
                                : '#639754',
                      }}
                    ></View>
                  </View>
                </View>
              </View>
            </View>
          );
        })}
        <View style={styles.scaleBarContainer}>
          <View style={{ width: '50%' }}></View>
          <View style={{ width: '50%', marginBottom: 10 }}>
            <ScaleBar />
          </View>
        </View>
      </View>

      <View style={styles.tableHeader}>
        <View style={styles.tableHeaderTitle}>
          <Text style={styles.tableHeaderTitleText1}>
            {uiData.header02}: {matchCategory}
          </Text>
        </View>
      </View>

      <View style={styles.legendContainer}>
        <View
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <View style={styles.legendLine}></View>
            <View style={styles.legendDot}></View>
            <View style={[styles.legendLine, { marginRight: 5 }]}></View>
          </View>
          <Text style={[styles.legendText, { marginRight: 10 }]}>{uiData.minimum_short}</Text>
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <View style={[styles.legendLine, { backgroundColor: '#3395E0' }]}></View>
            <View style={[styles.legendDot, { backgroundColor: '#3395E0' }]}></View>
            <View
              style={[styles.legendLine, { marginRight: 5, backgroundColor: '#3395E0' }]}
            ></View>
          </View>
          <Text style={[styles.legendText, { marginRight: 10 }]}>{uiData.average}</Text>
          <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <View style={[styles.legendLine, { backgroundColor: '#7CB662' }]}></View>
            <View style={[styles.legendDot, { backgroundColor: '#7CB662' }]}></View>
            <View
              style={[styles.legendLine, { marginRight: 5, backgroundColor: '#7CB662' }]}
            ></View>
          </View>
          <Text style={styles.legendText}>{uiData.maximum_short}</Text>
        </View>
      </View>

      <View
        style={{
          position: 'relative',
          paddingLeft: '5%',
          paddingRight: '5%',
          marginTop: 30,
          marginBottom: 40,
          width: '100%',
        }}
      >
        <View style={styles.gridContainer} zIndex={9999}>
          <View style={{ width: '50%' }}></View>
          <View style={{ width: '50%' }}>
            <TableGridGraph />
          </View>
        </View>

        {answersFromCategory.map(({ id, answer, points }, index) => {
          const minPoint = Math.min(...points);
          const maxPoint = Math.max(...points);
          const averagePoint = Math.round(points.reduce((acc, val) => acc + val, 0) / points.length);

          const minPointXPosition = calculateXPosition(minPoint);
          const nextEntry = answersFromCategory[index + 1];
          let nextDotXPosition = 0;

          if (nextEntry) {
            const nextMinPoint = Math.min(...nextEntry.points);
            nextDotXPosition = calculateXPosition(nextMinPoint);
          }

          return (
            <View key={id}>
              <View style={styles.tableContainer}>
                <View style={styles.tableFirstCol}>
                  <View style={[styles.tableFirstColAnswer, { justifyContent: 'flex-start' }]}>
                    <View style={styles.tableFirstColAnswerNumber}>
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 9,
                          fontWeight: 600,
                          lineHeight: 1.1,
                        }}
                      >
                        F{(id >= 10 ? id + '.' : '0' + id + '.')}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.tableFirstColAnswerText,
                        { width: '90%', height: 25, paddingBottom: 0 },
                      ]}
                    >
                      <Text
                        style={{
                          fontFamily: 'Inter',
                          fontSize: 8,
                          lineHeight: 1.2,
                        }}
                      >
                        {(() => {
                          const fullText = answer;
                          const maxLength = 110;
                          return fullText.length > maxLength
                            ? fullText.substring(0, maxLength) + '...'
                            : fullText;
                        })()}
                      </Text>
                    </View>
                  </View>
                </View>
                <View
                  style={[
                    styles.tableLastCol,
                    { backgroundColor: 'transparent', opacity: 1, borderBottom: 'none' },
                  ]}
                >
                  <View style={styles.tableLastColRows}>
                    {minPoint !== undefined && (
                      <View
                        style={{
                          height: 5,
                          width: 5,
                          backgroundColor: '#000',
                          borderRadius: 20,
                          position: 'absolute',
                          left: minPointXPosition,
                          top: 5,
                        }}
                      ></View>
                    )}
                    {nextEntry && minPoint !== undefined && nextEntry.points && (
                      <Svg height="45" width="100%">
                        <Line
                          x1={minPointXPosition + 2.5}
                          y1={7.5}
                          x2={nextDotXPosition + 2.5}
                          y2="75%"
                          strokeWidth={1.5}
                          stroke="#000"
                        />
                      </Svg>
                    )}
                  </View>
                </View>
              </View>
            </View>
          );
        })}

        <View
          style={{
            position: 'absolute',
            left: '6%',
            backgroundColor: 'transparent',
            opacity: 0,
            marginTop: -1,
          }}
        >
          {answersFromCategory.map(({ id, points }, index) => {
            const averagePoint = Math.round(
              points.reduce((acc, val) => acc + val, 0) / points.length
            );

            const averagePointXPosition = calculateXPosition(averagePoint);
            const nextEntry = answersFromCategory[index + 1];
            let nextDotXPosition = 0;

            if (nextEntry) {
              const nextAveragePoint = Math.round(
                nextEntry.points.reduce((acc, val) => acc + val, 0) / nextEntry.points.length
              );
              nextDotXPosition = calculateXPosition(nextAveragePoint);
            }

            return (
              <View
                key={id}
                style={{
                  paddingTop: 5.5,
                  paddingBottom: 5.5,
                }}
              >
                <View
                  style={[
                    styles.tableContainer,
                    { border: 'none', backgroundColor: 'transparent' },
                  ]}
                >
                  <View style={styles.tableFirstCol}></View>

                  <View
                    style={[
                      styles.tableLastCol,
                      { backgroundColor: 'transparent', opacity: 1, borderBottom: 'none' },
                    ]}
                  >
                    <View style={styles.tableLastColRows}>
                      {averagePoint !== undefined && (
                        <View
                          style={{
                            height: 5,
                            width: 5,
                            backgroundColor: '#3395E0',
                            borderRadius: 20,
                            position: 'absolute',
                            left: averagePointXPosition,
                            top: 5,
                          }}
                        ></View>
                      )}
                      {nextEntry && (
                        <Svg height="45" width="100%">
                          <Line
                            x1={averagePointXPosition + 2.5}
                            y1={7.5}
                            x2={nextDotXPosition + 2.5}
                            y2="75%"
                            strokeWidth={1.5}
                            stroke="#3395E0"
                          />
                        </Svg>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            );
          })}
        </View>

        <View
          style={{
            position: 'absolute',
            left: '6.75%',
            backgroundColor: 'transparent',
            opacity: 0,
            marginTop: -1,
          }}
        >
          {answersFromCategory.map(({ id, points }, index) => {
            const maxPoint = Math.max(...points);

            const maxPointXPosition = calculateXPosition(maxPoint);
            const nextEntry = answersFromCategory[index + 1];
            let nextDotXPosition = 0;

            if (nextEntry) {
              const nextMaxPoint = Math.max(...nextEntry.points);
              nextDotXPosition = calculateXPosition(nextMaxPoint);
            }

            return (
              <View
                key={id}
                style={{
                  paddingTop: 5.5,
                  paddingBottom: 5.5,
                }}
              >
                <View
                  style={[
                    styles.tableContainer,
                    { border: 'none', backgroundColor: 'transparent' },
                  ]}
                >
                  <View style={styles.tableFirstCol}></View>

                  <View
                    style={[
                      styles.tableLastCol,
                      { backgroundColor: 'transparent', opacity: 1, borderBottom: 'none' },
                    ]}
                  >
                    <View style={styles.tableLastColRows}>
                      {maxPoint !== undefined && (
                        <View
                          style={{
                            height: 5,
                            width: 5,
                            backgroundColor: '#7CB662',
                            borderRadius: 20,
                            position: 'absolute',
                            left: maxPointXPosition,
                            top: 5,
                          }}
                        ></View>
                      )}
                      {nextEntry && (
                        <Svg height="45" width="100%">
                          <Line
                            x1={maxPointXPosition + 2.5}
                            y1={7.5}
                            x2={nextDotXPosition + 2.5}
                            y2="75%"
                            strokeWidth={1.5}
                            stroke="#7CB662"
                          />
                        </Svg>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            );
          })}
        </View>

        <View style={styles.scaleBarContainer}>
          <View style={{ width: '50%' }}></View>
          <View style={{ width: '50%', marginBottom: 10 }}>
            <ScaleBarDiffGraph />
          </View>
        </View>
      </View>

      {questionsOn &&
        answersFromCategory.map(({ id, answer, points }, index) => {
          const averagePoint = Math.round(
            points.reduce((acc, val) => acc + val, 0) / points.length
          );

          let profilesWithPoints;

          if (
            add2GaugePage &&
            coverData &&
            Array.isArray(coverData.profile) &&
            coverData.profile.length === 2
          ) {
            const [firstName, secondName] = coverData.profile;

            const first = { profile: firstName, points: points[0] };
            const avg = { profile: uiData.average, points: averagePoint };
            const second = { profile: secondName, points: points[1] };

            profilesWithPoints = [first, avg, second];
          } else {
            profilesWithPoints = coverData.profile.map((profile, profileIndex) => ({
              profile,
              points: points[profileIndex],
            }));
            profilesWithPoints.push({ profile: uiData.average, points: averagePoint });
            profilesWithPoints.sort((a, b) => b.points - a.points);
          }

          return (
            <View key={id} wrap={false} style={{ marginTop: 30 }}>
              <View
                style={{
                  paddingLeft: '5%',
                  paddingRight: '5%',
                  width: '100%',
                }}
              >
                <Text
                  style={{
                    fontFamily: 'Inter',
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.1,
                    marginBottom: 5,
                  }}
                >
                  {uiData.header01}: {matchCategory}
                </Text>
                <Text
                  style={{
                    fontFamily: 'Inter',
                    fontSize: 10,
                    fontWeight: 400,
                    lineHeight: 1.1,
                  }}
                >
                  F{(id >= 10 ? id + '.' : '0' + id + '.')} {answer}
                </Text>
              </View>

              <View
                style={{
                  position: 'relative',
                  paddingLeft: '5%',
                  paddingRight: '5%',
                  marginTop: 20,
                  width: '100%',
                }}
              >
                <View style={styles.gridContainer} zIndex={9999}>
                  <View style={{ width: '50%' }}></View>
                  <View style={{ width: '50%' }}>
                    <TableGrid />
                  </View>
                </View>

                {profilesWithPoints.map((profileWithPoints, profileIndex) => {
                  const isAverage = profileWithPoints.profile === uiData.average;
                  return (
                    <View key={profileIndex}>
                      <View style={styles.tableContainer}>
                        <View style={styles.tableFirstCol}>
                          <View style={styles.tableFirstColAnswer}>
                            <View style={styles.tableFirstColAnswerText}>
                              <Text
                                style={{
                                  fontFamily: 'Inter',
                                  fontSize: 10,
                                  fontWeight: 500,
                                  lineHeight: 1.2,
                                  color: isAverage ? '#3395E0' : '#000',
                                }}
                              >
                                {profileWithPoints.profile}
                              </Text>
                            </View>
                            <View style={styles.tableFirstColAnswerPoints}>
                              <Text
                                style={{
                                  fontFamily: 'Inter',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: isAverage ? '#3395E0' : '#000',
                                }}
                              >
                                {profileWithPoints.points}
                              </Text>
                            </View>
                          </View>
                        </View>
                        <View style={styles.tableLastCol}>
                          <View style={styles.tableLastColRows}>
                            <View
                              style={{
                                height: 15,
                                borderTopRightRadius: 5,
                                borderBottomRightRadius: 5,
                                width: profileWithPoints.points + '%',
                                backgroundColor: isAverage
                                  ? '#3395E0'
                                  : profileWithPoints.points <= 35
                                    ? '#dc2626'
                                    : profileWithPoints.points <= 50
                                      ? '#eac714'
                                      : profileWithPoints.points <= 65
                                        ? '#7bb662'
                                        : '#639754',
                              }}
                            ></View>
                          </View>
                        </View>
                      </View>
                    </View>
                  );
                })}
                <View style={styles.scaleBarContainer}>
                  <View style={{ width: '50%' }}></View>
                  <View style={{ width: '50%', marginBottom: 10 }}>
                    <ScaleBar />
                  </View>
                </View>
              </View>
            </View>
          );
        })}
    </>
  );
};

export default OverviewTable
